import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import { boardColor, pruneRecentBoards, readRecentBoardIds } from '@/entities/board';
import { type AccessibleBoard, useMyBoards } from '@/entities/collab';
import { useViewer } from '@/entities/viewer';
import { useIsMobile, useIsTouchUi } from '@/shared/lib/media';
import { originStateFrom } from '@/shared/lib/origin';
import { useShortcutHandler } from '@/shared/lib/shortcuts';
import { Dialog, IconButton, Icons, Popover } from '@/shared/ui';

import styles from './BoardDeck.module.css';

interface Props {
  /** The current board's workspace (``null``/absent for a board-only guest). */
  workspaceId?: string | null;
  currentBoardId: string;
  workspacePath: string;
  /** Creating a board is the page's dialog; the deck only asks for it. */
  onNewBoard?: (() => void) | undefined;
}

const RECENT_SHOWN = 5;

/** Fast board switching: own button beside the title, search + recent + favorites. */
export function BoardDeck({ workspaceId, currentBoardId, workspacePath, onNewBoard }: Props) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const [open, setOpen] = useState(false);
  useShortcutHandler('switch-board', () => {
    setOpen(true);
  });

  const panel = (close: () => void) => (
    <DeckPanel
      workspaceId={workspaceId}
      currentBoardId={currentBoardId}
      workspacePath={workspacePath}
      onNewBoard={onNewBoard}
      onDone={close}
    />
  );

  if (mobile) {
    return (
      <>
        <IconButton
          label={t('boardDeck.open')}
          icon={<Icons.switcher />}
          aria-haspopup="dialog"
          onClick={() => {
            setOpen(true);
          }}
        />
        {open && (
          <Dialog
            title={t('boardDeck.title')}
            onClose={() => {
              setOpen(false);
            }}
          >
            {panel(() => {
              setOpen(false);
            })}
          </Dialog>
        )}
      </>
    );
  }
  return (
    <Popover
      label={t('boardDeck.title')}
      width={340}
      open={open}
      onOpenChange={setOpen}
      trigger={(props) => (
        <IconButton label={t('boardDeck.open')} icon={<Icons.switcher />} {...props} />
      )}
    >
      {panel}
    </Popover>
  );
}

function DeckPanel({
  currentBoardId,
  workspacePath,
  onNewBoard,
  onDone,
}: Props & { onDone: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const viewer = useViewer();
  const userId = viewer.status === 'authenticated' ? viewer.user.id : null;
  // Every accessible board, whichever workspace or grant provides it (each once).
  const boards = useMyBoards({ limit: 500 });
  const [query, setQuery] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);

  const touch = useIsTouchUi();
  useEffect(() => {
    // keyboard-first: type to filter right away - but not on a touch screen, where the focus would
    // raise the on-screen keyboard before the person chose to search
    if (!touch) search.current?.focus();
  }, [touch]);

  const list = useMemo(() => boards.data ?? [], [boards.data]);
  // Forget recents that are gone (deleted / no longer accessible) - they are never shown anyway.
  useEffect(() => {
    if (userId && boards.isSuccess) pruneRecentBoards(userId, new Set(list.map((b) => b.id)));
  }, [userId, boards.isSuccess, list]);

  const sections = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    const byId = new Map(list.map((board) => [board.id, board]));
    const current = byId.get(currentBoardId) ?? null;
    if (needle) {
      return {
        current: null,
        found: list.filter((board) => board.name.toLocaleLowerCase().includes(needle)),
        recent: [] as AccessibleBoard[],
        favorites: [] as AccessibleBoard[],
        others: [] as AccessibleBoard[],
      };
    }
    const used = new Set<string>(current ? [current.id] : []);
    const take = (boardsToTake: AccessibleBoard[]) => {
      const taken = boardsToTake.filter((board) => !used.has(board.id));
      taken.forEach((board) => used.add(board.id));
      return taken;
    };
    const recentIds = userId ? readRecentBoardIds(userId) : [];
    const recent = take(
      recentIds.flatMap((id) => byId.get(id) ?? []).slice(0, RECENT_SHOWN + 1),
    ).slice(0, RECENT_SHOWN);
    const favorites = take(list.filter((board) => board.is_favorite));
    const others = take(list);
    return { current, found: null, recent, favorites, others };
  }, [list, query, currentBoardId, userId]);

  const go = (board: AccessibleBoard) => {
    onDone();
    // switching boards keeps the origin the current one was opened from
    if (board.id !== currentBoardId) {
      void navigate(`/app/boards/${board.id}`, { state: originStateFrom(location) });
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = Array.from(root.current?.querySelectorAll<HTMLElement>('[data-deck-item]') ?? []);
    const order = [search.current, ...items].filter((el): el is HTMLElement => el !== null);
    const at = order.indexOf(document.activeElement as HTMLElement);
    const next =
      order[Math.max(0, Math.min(order.length - 1, at + (event.key === 'ArrowDown' ? 1 : -1)))];
    if (next) {
      event.preventDefault();
      next.focus();
    }
  };

  const row = (board: AccessibleBoard, current = false, withContext = false) => (
    <li key={board.id}>
      <button
        type="button"
        className={`${styles.row} ${current ? styles.current : ''}`}
        data-deck-item
        aria-current={current ? 'true' : undefined}
        onClick={() => {
          go(board);
        }}
      >
        <span
          className={styles.dot}
          style={{ ['--board-color' as string]: boardColor(board) }}
          aria-hidden
        />
        <span className={styles.name}>
          {board.name}
          {withContext && board.workspace_type === 'team' && board.workspace_name && (
            <span className={styles.context}> · {board.workspace_name}</span>
          )}
        </span>
        {board.is_favorite && (
          <span className={styles.star} aria-hidden>
            <Icons.star />
          </span>
        )}
        {current && <span className={styles.badge}>{t('boardDeck.current')}</span>}
      </button>
    </li>
  );

  const group = (title: string, items: AccessibleBoard[], withContext = false) =>
    items.length > 0 && (
      <section className={styles.group} aria-label={title}>
        <h3 className={styles.groupTitle}>{title}</h3>
        <ul className={styles.list}>{items.map((board) => row(board, false, withContext))}</ul>
      </section>
    );

  // The rest, grouped: personal boards, each team workspace, boards shared with the viewer
  // (a guest never learns the parent workspace, so those carry no workspace name).
  const othersGroups = useMemo(() => {
    const personal = sections.others.filter((b) => b.workspace_type === 'personal');
    const shared = sections.others.filter((b) => b.workspace_type === null);
    const byWorkspace = new Map<string, AccessibleBoard[]>();
    for (const board of sections.others) {
      if (board.workspace_type !== 'team') continue;
      const key = board.workspace_name ?? '';
      byWorkspace.set(key, [...(byWorkspace.get(key) ?? []), board]);
    }
    return { personal, shared, teams: [...byWorkspace].sort(([a], [b]) => a.localeCompare(b)) };
  }, [sections.others]);

  const empty =
    boards.isSuccess &&
    !sections.current &&
    (sections.found?.length ?? 0) +
      sections.recent.length +
      sections.favorites.length +
      sections.others.length ===
      0;

  return (
    <div ref={root} className={styles.panel} onKeyDown={onKeyDown}>
      <input
        ref={search}
        type="search"
        className={styles.search}
        aria-label={t('boardDeck.search')}
        placeholder={t('boardDeck.search')}
        value={query}
        autoComplete="off"
        onChange={(event) => {
          setQuery(event.target.value);
        }}
      />
      <div className={styles.scroll}>
        {boards.isPending && <p className={styles.note}>…</p>}
        {sections.current && (
          <section className={styles.group} aria-label={t('boardDeck.current')}>
            <ul className={styles.list}>{row(sections.current, true)}</ul>
          </section>
        )}
        {sections.found && group(t('boardDeck.results'), sections.found, true)}
        {group(t('boardDeck.recent'), sections.recent)}
        {group(t('boardDeck.favorites'), sections.favorites)}
        {group(t('boardDeck.personal'), othersGroups.personal)}
        {othersGroups.teams.map(([name, items]) => group(name, items))}
        {group(t('boardDeck.sharedWithMe'), othersGroups.shared)}
        {empty && (
          <p className={styles.note} role="status">
            {t('boardDeck.none')}
          </p>
        )}
      </div>
      <div className={styles.footer}>
        {onNewBoard && (
          <button
            type="button"
            className={styles.footerAction}
            onClick={() => {
              onDone();
              onNewBoard();
            }}
          >
            <Icons.plus /> {t('boardDeck.newBoard')}
          </button>
        )}
        <Link to={workspacePath} className={styles.footerAction} onClick={onDone}>
          <Icons.board /> {t('boardDeck.allBoards')}
        </Link>
      </div>
    </div>
  );
}
